// src/pages/Retenciones.jsx
import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

// Para exportar a Excel
import * as XLSX from 'xlsx';

// Para exportar a PDF
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// Importa tu logo local (ajusta la ruta si es distinta)
import logo from '../assets/logo.png';

// Datos de la empresa
const NOMBRE_GLOBAL = "GLOBAL Frenos & Multiservicios";
const RIF_GLOBAL = "RIF: J506844261";
const DIRECCION_GLOBAL = "Calle Rivas Dávila, Edif. Ribas, Piso PB, Locales #2, #3, #4, Urb. La Calera, La Victoria, Edo. Aragua";

const Retenciones = () => {
  // Estado con TODAS las retenciones
  const [retenciones, setRetenciones] = useState([]);

  // Filtros
  const [tipo, setTipo] = useState('venta'); // 'venta' o 'factura'
  const [filtroTexto, setFiltroTexto] = useState('');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');

  // Al montar, cargamos las retenciones
  useEffect(() => {
    const fetchRetenciones = async () => {
      const snap = await getDocs(collection(db, 'retenciones'));
      // Convertimos la fecha de Firestore Timestamp a Date
      const data = snap.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        fecha: doc.data().fecha ? doc.data().fecha.toDate() : null
      }));
      setRetenciones(data);
    };
    fetchRetenciones();
  }, []);

  // ===========================================================
  // FUNCIONES DE FILTRO
  // ===========================================================
  const retencionesFiltradas = retenciones.filter(r => {
    if (r.tipo !== tipo) return false;

    const textoMin = filtroTexto.toLowerCase();
    const coincideTexto = (
      (r.proveedor && r.proveedor.toLowerCase().includes(textoMin)) ||
      (r.cliente && r.cliente.toLowerCase().includes(textoMin)) ||
      (r.rif && r.rif.toLowerCase().includes(textoMin))
    );
    if (!coincideTexto && filtroTexto.trim() !== '') return false;

    if (r.fecha) {
      const fechaStr = r.fecha.toISOString().split('T')[0];
      if (fechaInicio && fechaStr < fechaInicio) return false;
      if (fechaFin && fechaStr > fechaFin) return false;
    }

    return true;
  });

  // ===========================================================
  // EXPORTAR EXCEL (LISTADO COMPLETO FILTRADO)
  // ===========================================================
  const exportarExcel = () => {
    const datosFiltrados = retencionesFiltradas.map(r => ({
      Fecha: r.fecha ? r.fecha.toLocaleDateString() : '',
      Tipo: r.tipo,
      ClienteProveedor: r.proveedor || r.cliente || '',
      RIF: r.rif || '',
      Monto: (r.monto || 0).toFixed(2),
      Retenido: (r.retencion || 0).toFixed(2),
      Total: (r.total || 0).toFixed(2)
    }));

    const ws = XLSX.utils.json_to_sheet(datosFiltrados);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Retenciones');
    XLSX.writeFile(wb, 'Retenciones.xlsx');
  };

  // ===========================================================
  // FUNCIONES PARA PDF
  // ===========================================================
  const insertarEncabezadoPie = (doc) => {
    // Insertar LOGO arriba derecha
    doc.addImage(logo, 'PNG', 150, 10, 40, 15);

    // Pie de página (abajo)
    const pageHeight = doc.internal.pageSize.getHeight();
    doc.setFontSize(10);
    doc.text(NOMBRE_GLOBAL, 14, pageHeight - 30);
    doc.text(RIF_GLOBAL, 14, pageHeight - 25);
    doc.text(DIRECCION_GLOBAL, 14, pageHeight - 20);
  };

  // (A) Exportar PDF listado completo
  const exportarPDF = () => {
    const doc = new jsPDF();

    insertarEncabezadoPie(doc);

    doc.setFontSize(16);
    doc.text(`Listado de Retenciones (${tipo})`, 14, 35);

    const filas = retencionesFiltradas.map(r => ([
      r.fecha ? r.fecha.toLocaleDateString() : '',
      r.tipo,
      r.proveedor || r.cliente || '',
      r.rif || '',
      `$${(r.monto || 0).toFixed(2)}`,
      `$${(r.retencion || 0).toFixed(2)}`,
      `$${(r.total || 0).toFixed(2)}`
    ]));

    autoTable(doc, {
      head: [['Fecha', 'Tipo', 'Proveedor/Cliente', 'RIF', 'Monto', 'Retenido', 'Total']],
      body: filas,
      startY: 45
    });

    doc.save('Retenciones.pdf');
  };

  // (B) Exportar PDF individual
  const exportarPDFIndividual = (r) => {
    const doc = new jsPDF();

    insertarEncabezadoPie(doc);

    doc.setFontSize(16);
    doc.text(`Retención: ${r.id}`, 14, 35);

    const filas = [
      ['Fecha', r.fecha ? r.fecha.toLocaleDateString() : '---'],
      ['Tipo', r.tipo],
      [r.tipo === 'venta' ? 'Cliente' : 'Proveedor', r.proveedor || r.cliente || '---'],
      ['RIF', r.rif || '---'],
      ['Monto (c/IVA)', `$${(r.monto || 0).toFixed(2)}`],
      ['Retenido', `$${(r.retencion || 0).toFixed(2)}`],
      ['Total', `$${(r.total || 0).toFixed(2)}`]
    ];

    autoTable(doc, {
      head: [],
      body: filas,
      startY: 45,
      theme: 'grid'
    });

    doc.save(`Retencion_${r.id}.pdf`);
  };

  // ===========================================================
  // RENDER
  // ===========================================================
  return (
    <div className="retenciones-container">
      <h2>Retenciones ({tipo === 'venta' ? 'Ventas' : 'Facturas'})</h2>

      {/* FILTROS */}
      <div
        className="retenciones-filtros"
        style={{ marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}
      >
        {/* Tipo */}
        <div>
          <label>Tipo:</label><br />
          <select value={tipo} onChange={(e) => setTipo(e.target.value)}>
            <option value="venta">Ventas</option>
            <option value="factura">Facturas</option>
          </select>
        </div>

        {/* Fecha inicio */}
        <div>
          <label>Desde:</label><br />
          <input
            type="date"
            value={fechaInicio}
            onChange={(e) => setFechaInicio(e.target.value)}
          />
        </div>

        {/* Fecha fin */}
        <div>
          <label>Hasta:</label><br />
          <input
            type="date"
            value={fechaFin}
            onChange={(e) => setFechaFin(e.target.value)}
          />
        </div>

        {/* Texto filtro */}
        <div>
          <label>Buscar:</label><br />
          <input
            type="text"
            placeholder="Proveedor/Cliente o RIF"
            value={filtroTexto}
            onChange={(e) => setFiltroTexto(e.target.value)}
          />
        </div>

        {/* Botones export */}
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
          <button onClick={exportarExcel}>Exportar Excel</button>
          <button onClick={exportarPDF} style={{ marginTop: '5px' }}>Exportar PDF</button>
        </div>
      </div>

      {/* LISTADO DE RETENCIONES FILTRADAS */}
      <div className="retenciones-lista">
        {retencionesFiltradas.length === 0 ? (
          <p>No hay retenciones para este filtro.</p>
        ) : (
          retencionesFiltradas.map(r => (
            <div
              className="retencion-card"
              key={r.id}
              style={{
                marginBottom: '15px',
                padding: '10px',
                background: '#1a1a1a',
                borderRadius: '6px'
              }}
            >
              <p><strong>ID:</strong> {r.id}</p>
              <p><strong>Fecha:</strong> {r.fecha ? r.fecha.toLocaleDateString() : ''}</p>
              <p><strong>{r.tipo === 'venta' ? 'Cliente' : 'Proveedor'}:</strong> {r.proveedor || r.cliente || '---'}</p>
              <p><strong>RIF:</strong> {r.rif || '---'}</p>
              <p><strong>Monto (c/IVA):</strong> ${r.monto?.toFixed(2) || '0.00'}</p>
              <p><strong>Retenido:</strong> ${r.retencion?.toFixed(2) || '0.00'}</p>
              <p><strong>Total:</strong> ${r.total?.toFixed(2) || '0.00'}</p>

              {/* BOTÓN PARA PDF INDIVIDUAL */}
              <button
                onClick={() => exportarPDFIndividual(r)}
                style={{ marginTop: '8px' }}
              >
                Exportar PDF (Individual)
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Retenciones;
